import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const RegistrationRoleSelection = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(null);

  const roles = [
    {
      id: 'student',
      title: 'Étudiant',
      description: 'Je suis un élève cherchant des recommandations d\'orientation personnalisées',
      icon: 'GraduationCap',
      color: 'blue',
      benefits: [
        'Quiz interactifs d\'orientation',
        'Recommandations de filières personnalisées',
        'Exploration de domaines de carrière',
        'Suivi de progression et badges'
      ],
      path: '/student-registration'
    },
    {
      id: 'parent',
      title: 'Parent',
      description: 'Je suis un parent souhaitant suivre l\'orientation de mon enfant',
      icon: 'Users',
      color: 'green',
      benefits: [
        'Suivi de la progression académique',
        'Rapports de performance détaillés',
        'Communication avec les conseillers',
        'Notifications de réalisations'
      ],
      path: '/parent-registration'
    },
    {
      id: 'counselor',
      title: 'Conseiller d\'Orientation',
      description: 'Je suis un conseiller professionnel gérant plusieurs étudiants',
      icon: 'Briefcase',
      color: 'purple',
      benefits: [
        'Gestion de cas étudiants',
        'Outils d\'intervention professionnels',
        'Analytics et rapports institutionnels',
        'Communication de groupe'
      ],
      path: '/counselor-registration'
    }
  ];

  const handleRoleSelect = (role) => {
    setSelectedRole(role?.id);
  };

  const handleContinue = () => {
    const role = roles?.find(r => r?.id === selectedRole);
    if (role) {
      navigate(role?.path);
    }
  };

  const getColorClasses = (color, isSelected) => {
    const baseClasses = {
      blue: isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300',
      green: isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300',
      purple: isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
    };
    return baseClasses?.[color] || baseClasses?.blue;
  };

  const getIconColor = (color) => {
    const colors = {
      blue: '#3b82f6',
      green: '#10b981',
      purple: '#8b5cf6'
    };
    return colors?.[color] || colors?.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="content-area">
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12 lg:py-16">
          <button
            onClick={() => navigate('/landing-page')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 mb-6"
          >
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm md:text-base">Retour à l'accueil</span>
          </button>

          <div className="text-center mb-10 md:mb-12 lg:mb-16">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Choisissez Votre Profil
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Sélectionnez le type de compte qui correspond à votre situation pour une expérience personnalisée
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-10">
            {roles?.map((role) => (
              <div
                key={role?.id}
                onClick={() => handleRoleSelect(role)}
                className={`cursor-pointer rounded-xl p-6 md:p-8 border-2 transition-all duration-200 ${
                  getColorClasses(role?.color, selectedRole === role?.id)
                } hover:shadow-lg`}
              >
                <div className="flex flex-col items-center text-center mb-6">
                  <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center mb-4 ${
                    selectedRole === role?.id
                      ? `bg-${role?.color}-100`
                      : 'bg-gray-100'
                  }`}>
                    <Icon name={role?.icon} size={32} color={getIconColor(role?.color)} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                    {role?.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600">
                    {role?.description}
                  </p>
                </div>

                <div className="space-y-3">
                  <p className="text-sm font-semibold text-gray-900 mb-3">Avantages inclus :</p>
                  {role?.benefits?.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Icon name="Check" size={16} color={getIconColor(role?.color)} className="flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>

                {selectedRole === role?.id && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-center gap-2 text-sm font-medium" style={{ color: getIconColor(role?.color) }}>
                      <Icon name="CheckCircle" size={18} />
                      <span>Sélectionné</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-center">
            <Button
              variant="default"
              size="lg"
              onClick={handleContinue}
              disabled={!selectedRole}
              iconName="ArrowRight"
              iconPosition="right"
              className="px-8 md:px-12"
            >
              Continuer l'Inscription
            </Button>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Déjà un compte ?{' '}
              <button
                onClick={() => navigate('/student-dashboard')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Se connecter
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationRoleSelection;